/**
 * The haulage plan as a sender reads it: one row per route, one cell per stop.
 *
 * Pure on purpose, and separate from the planning list that offers the download: this file is the
 * one export that leaves the building, so the things that would embarrass us in front of a sender
 * -- a stop out of order, or a time that reads as an impossibly early morning when it is really
 * the following day -- are decided here where a test can hold them still.
 *
 * What it deliberately does not carry: cost, load factor, empty legs, driver, vehicle. Those are
 * ours. A sender needs which route, which day, where, and when.
 */
import { differenceInDays, format, startOfDay } from "date-fns"

import { PlannedRoute, PlannedStop } from "@/types/collections"
import { getSlotTypeLabel } from "@/lib/slotTypeDisplay"

/** The columns before the per-stop ones. Stop 1..N are appended to suit the longest route. */
export const HAULAGE_PLAN_BASE_HEADERS = ["Date", "Day", "Route Code", "Total Stops"]

/** The location fields worth preferring over the copies denormalised onto a stop. */
type SiteLike = {
  site_name?: string | null
  address?: string | null
  postcode?: string | null
}

const offsetSuffix = (days: number): string => (days > 0 ? ` (+${days}d)` : "")

const dayOffset = (time: Date, routeDate: Date): number =>
  differenceInDays(startOfDay(time), startOfDay(routeDate))

/**
 * The arrive-depart window, with the day marked where it is not the day the route starts.
 *
 * Times are the point of this file, so a stop that lands after the route's own date says so
 * rather than reading as an impossibly early morning. Where both ends share an offset the marker
 * is written once at the end; where they differ -- a stop whose service crosses midnight -- each
 * end carries its own, since one trailing marker could not say which end it belonged to.
 */
export function formatStopWindow(stop: PlannedStop, routeDate: Date): string {
  return formatTimeWindow(stop.plannedArrival, stop.plannedDeparture, routeDate)
}

/** The same window for any two times on a route -- a stop's, or the route's own start and end. */
export function formatTimeWindow(start: Date, end: Date, routeDate: Date): string {
  const startOffset = dayOffset(start, routeDate)
  const endOffset = dayOffset(end, routeDate)

  if (startOffset === endOffset) {
    return `${format(start, "HH:mm")}-${format(end, "HH:mm")}${offsetSuffix(startOffset)}`
  }
  return `${formatRouteTime(start, routeDate)}-${formatRouteTime(end, routeDate)}`
}

/** One end of a window on its own, for a layout that gives arrival and departure a column each. */
export function formatRouteTime(time: Date, routeDate: Date): string {
  return `${format(time, "HH:mm")}${offsetSuffix(dayOffset(time, routeDate))}`
}

/**
 * One stop as a single cell: what kind of stop, where, and when.
 *
 * Kept to one line rather than stacked with newlines, so the file is readable without the sender
 * having to turn wrap-text on. The pipe before the window is doing real work: an address is full
 * of commas, and the time is the part a sender is actually looking for, so it gets set off rather
 * than left to trail off the end of a list. Parentheses would have nested around the day marker.
 */
export function formatStopCell(stop: PlannedStop, routeDate: Date, site?: SiteLike): string {
  const where = [
    site?.site_name || stop.name || "",
    site?.address || stop.address || "",
    site?.postcode || stop.postcode || "",
  ]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ")

  const label = getSlotTypeLabel(stop.slotType)
  return `${label}: ${where || "Unknown site"} | ${formatStopWindow(stop, routeDate)}`
}

/**
 * The whole table: headers sized to the longest route, then one row per route.
 *
 * Routes run in date then code order and stops in the order the route runs them. Every stop is
 * listed, not only the haulage ones, because the stops either side are what make the timings
 * legible. Short routes leave their trailing Stop columns empty, so a column always means the
 * same position in the run -- which is also what keeps the file sortable in Excel, since every
 * route is exactly one row.
 *
 * `getSite` is how the caller reaches the locations it has already loaded; a stop carries its own
 * copy of the name and address, but the location row is the one that gets corrected.
 */
export function buildHaulagePlanTable(
  routes: PlannedRoute[],
  getSite?: (locationId: string) => SiteLike | undefined,
): { headers: string[]; rows: string[][] } {
  const sortedRoutes = [...routes].sort(
    (a, b) => a.date.getTime() - b.date.getTime() || a.code.localeCompare(b.code),
  )

  const routeStops = sortedRoutes.map((route) =>
    [...(route.stops ?? [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
  )

  const maxStops = routeStops.reduce((most, stops) => Math.max(most, stops.length), 0)

  const headers = [
    ...HAULAGE_PLAN_BASE_HEADERS,
    ...Array.from({ length: maxStops }, (_, i) => `Stop ${i + 1}`),
  ]

  const rows = sortedRoutes.map((route, routeIndex) => {
    const stops = routeStops[routeIndex]
    const cells = stops.map((stop) => formatStopCell(stop, route.date, getSite?.(stop.locationId)))

    return [
      format(route.date, "yyyy-MM-dd"),
      format(route.date, "EEEE"),
      route.code,
      stops.length.toString(),
      ...cells,
      // Pad so every row is the width of the header: a Stop column has to mean the same
      // position in the run on every row, or the file cannot be read down a column.
      ...Array.from({ length: maxStops - cells.length }, () => ""),
    ]
  })

  return { headers, rows }
}
